import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  LinearProgress,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Chip,
  Collapse,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Error as ErrorIcon,
  Refresh,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import api from "../../services/api";

const PaymentSettings = () => {
  const { storeId } = useParams();
  const [settings, setSettings] = useState({
    square: {
      enabled: false,
      accessToken: "",
      locationId: "",
      environment: "sandbox",
      hasCredentials: false,
    },
    paypay: {
      enabled: false,
      apiKey: "",
      apiSecret: "",
      merchantId: "",
      environment: "sandbox",
      hasCredentials: false,
    },
    printer: {
      receiptPrinterHost: "",
      receiptPrinterPort: 9100,
      kitchenPrinterHost: "",
      kitchenPrinterPort: 9100,
      cashDrawerEnabled: true,
    },
  });

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    square: true,
    paypay: true,
    printer: false,
  });

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/stores/${storeId}/payment-settings`);
      if (response.data) {
        setSettings(response.data);
      }
    } catch (err) {
      console.error("決済設定の取得に失敗しました:", err);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSquareChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSettings((prev) => ({
      ...prev,
      square: {
        ...prev.square,
        [field]: value,
      },
    }));
  };

  const handlePayPayChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSettings((prev) => ({
      ...prev,
      paypay: {
        ...prev.paypay,
        [field]: value,
      },
    }));
  };

  const handlePrinterChange = (field) => (e) => {
    const value = e.target.type === "checkbox"
      ? e.target.checked
      : e.target.type === "number"
        ? parseInt(e.target.value, 10)
        : e.target.value;
    setSettings((prev) => ({
      ...prev,
      printer: {
        ...prev.printer,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      await api.put(`/api/stores/${storeId}/payment-settings`, settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      // 更新後に再取得
      await fetchSettings();
    } catch (err) {
      console.error("決済設定の更新に失敗しました:", err);
      setError(err.response?.data?.message || "決済設定の更新に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (gateway) => {
    try {
      setTesting((prev) => ({ ...prev, [gateway]: true }));
      setTestResults((prev) => ({ ...prev, [gateway]: null }));

      const response = await api.post(`/api/stores/${storeId}/payment-settings/test`, {
        gateway,
      });

      setTestResults((prev) => ({
        ...prev,
        [gateway]: response.data,
      }));
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [gateway]: {
          success: false,
          message: err.response?.data?.message || "接続テストに失敗しました",
        },
      }));
    } finally {
      setTesting((prev) => ({ ...prev, [gateway]: false }));
    }
  };

  const toggleShowSecret = (field) => {
    setShowSecrets((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const renderTestResult = (gateway) => {
    const result = testResults[gateway];
    if (!result) return null;

    return (
      <Alert
        severity={result.success ? "success" : "error"}
        sx={{ mt: 2 }}
        icon={result.success ? <CheckCircle /> : <ErrorIcon />}
      >
        {result.message}
        {result.locations && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">利用可能なロケーション:</Typography>
            {result.locations.map((loc) => (
              <Chip
                key={loc.id}
                label={`${loc.name} (${loc.id})`}
                size="small"
                sx={{ mr: 1, mt: 0.5 }}
              />
            ))}
          </Box>
        )}
      </Alert>
    );
  };

  return (
    <Box aria-busy={loading || saving}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Typography variant="h4" gutterBottom>
        決済設定
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        クレジットカード（Square）やQRコード決済（PayPay）の設定を行います。
        APIキーなどの機密情報は暗号化して保存されます。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          設定を更新しました
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Square設定 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSection("square")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="h6">
                      Square（クレジットカード）
                    </Typography>
                    {settings.square.hasCredentials && (
                      <Chip
                        label="設定済み"
                        color="success"
                        size="small"
                      />
                    )}
                  </Box>
                  <IconButton size="small">
                    {expandedSections.square ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedSections.square}>
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.square.enabled}
                          onChange={handleSquareChange("enabled")}
                          disabled={saving}
                        />
                      }
                      label="Square決済を有効にする"
                    />

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="アクセストークン"
                          type={showSecrets.squareToken ? "text" : "password"}
                          value={settings.square.accessToken}
                          onChange={handleSquareChange("accessToken")}
                          disabled={saving}
                          placeholder={
                            settings.square.hasCredentials
                              ? "変更する場合のみ入力"
                              : "sq0atp-xxxxx..."
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => toggleShowSecret("squareToken")}
                                  edge="end"
                                >
                                  {showSecrets.squareToken ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="ロケーションID"
                          value={settings.square.locationId}
                          onChange={handleSquareChange("locationId")}
                          disabled={saving}
                          placeholder="LXXXXXXXXXXXXXX"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          select
                          label="環境"
                          value={settings.square.environment}
                          onChange={handleSquareChange("environment")}
                          disabled={saving}
                          SelectProps={{ native: true }}
                        >
                          <option value="sandbox">サンドボックス（テスト）</option>
                          <option value="production">本番</option>
                        </TextField>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => testConnection("square")}
                        disabled={testing.square || !settings.square.enabled}
                        startIcon={
                          testing.square ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Refresh />
                          )
                        }
                      >
                        接続テスト
                      </Button>
                    </Box>

                    {renderTestResult("square")}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

          {/* PayPay設定 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSection("paypay")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="h6">
                      PayPay（QRコード決済）
                    </Typography>
                    {settings.paypay.hasCredentials && (
                      <Chip
                        label="設定済み"
                        color="success"
                        size="small"
                      />
                    )}
                  </Box>
                  <IconButton size="small">
                    {expandedSections.paypay ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedSections.paypay}>
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.paypay.enabled}
                          onChange={handlePayPayChange("enabled")}
                          disabled={saving}
                        />
                      }
                      label="PayPay決済を有効にする"
                    />

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="APIキー"
                          type={showSecrets.paypayKey ? "text" : "password"}
                          value={settings.paypay.apiKey}
                          onChange={handlePayPayChange("apiKey")}
                          disabled={saving}
                          placeholder={
                            settings.paypay.hasCredentials
                              ? "変更する場合のみ入力"
                              : ""
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => toggleShowSecret("paypayKey")}
                                  edge="end"
                                >
                                  {showSecrets.paypayKey ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="APIシークレット"
                          type={showSecrets.paypaySecret ? "text" : "password"}
                          value={settings.paypay.apiSecret}
                          onChange={handlePayPayChange("apiSecret")}
                          disabled={saving}
                          placeholder={
                            settings.paypay.hasCredentials
                              ? "変更する場合のみ入力"
                              : ""
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => toggleShowSecret("paypaySecret")}
                                  edge="end"
                                >
                                  {showSecrets.paypaySecret ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="加盟店ID"
                          value={settings.paypay.merchantId}
                          onChange={handlePayPayChange("merchantId")}
                          disabled={saving}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          select
                          label="環境"
                          value={settings.paypay.environment}
                          onChange={handlePayPayChange("environment")}
                          disabled={saving}
                          SelectProps={{ native: true }}
                        >
                          <option value="sandbox">サンドボックス（テスト）</option>
                          <option value="production">本番</option>
                        </TextField>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => testConnection("paypay")}
                        disabled={testing.paypay || !settings.paypay.enabled}
                        startIcon={
                          testing.paypay ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Refresh />
                          )
                        }
                      >
                        接続テスト
                      </Button>
                    </Box>

                    {renderTestResult("paypay")}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

          {/* プリンター設定 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSection("printer")}
                >
                  <Typography variant="h6">プリンター設定</Typography>
                  <IconButton size="small">
                    {expandedSections.printer ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedSections.printer}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      レシートプリンター
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          label="IPアドレス"
                          value={settings.printer.receiptPrinterHost}
                          onChange={handlePrinterChange("receiptPrinterHost")}
                          disabled={saving}
                          placeholder="192.168.1.100"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="ポート"
                          type="number"
                          value={settings.printer.receiptPrinterPort}
                          onChange={handlePrinterChange("receiptPrinterPort")}
                          disabled={saving}
                          inputProps={{ min: 1, max: 65535 }}
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      キッチンプリンター
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          label="IPアドレス"
                          value={settings.printer.kitchenPrinterHost}
                          onChange={handlePrinterChange("kitchenPrinterHost")}
                          disabled={saving}
                          placeholder="192.168.1.101"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="ポート"
                          type="number"
                          value={settings.printer.kitchenPrinterPort}
                          onChange={handlePrinterChange("kitchenPrinterPort")}
                          disabled={saving}
                          inputProps={{ min: 1, max: 65535 }}
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.printer.cashDrawerEnabled}
                          onChange={handlePrinterChange("cashDrawerEnabled")}
                          disabled={saving}
                        />
                      }
                      label="キャッシュドロアを使用する"
                    />
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

          {/* 保存ボタン */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={saving}
            >
              {saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "設定を保存"
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default PaymentSettings;
